import { Prose } from '../../components/Prose'
import { useState } from 'react'
import {
  useActivities, useAddActivity, useAdjustEnergy, useDeleteActivity, useMeasurements,
  useResetEnergy,
} from '../../api/hooks'
import { todayIso } from '../../lib/format'
import { ageFrom, leanMassKg, rfm, type Sex } from '../../lib/body'
import {
  ACTIVITIES, ACTIVITY_BY_KEY, GOAL_LABEL, activityKcal, bmr, bmrRange, calorieTarget,
  dailyExpenditure, goalOf, proteinTarget, rateCheck, rhoEff, weightSlope,
} from '../../lib/energy'
import type { Profile, WeightEntry } from '../../lib/metrics'

const k0 = (v: number) => Math.round(v).toLocaleString('tr-TR')
/** Katalog gruplara ayrılmış halde — 40 satırlık düz liste seçilemez. */
const GROUPS = [...new Set(ACTIVITIES.map((a) => a.group))]

export function EnergyCard({ profile, weights, weightKg }: {
  profile: Profile; weights: WeightEntry[]; weightKg: number
}) {
  const activities = useActivities()
  const measurements = useMeasurements()
  const add = useAddActivity()
  const del = useDeleteActivity()
  const adjust = useAdjustEnergy()
  const reset = useResetEnergy()

  const [key, setKey] = useState('tennis_singles')
  const [minutes, setMinutes] = useState('60')
  const [kcal, setKcal] = useState('')
  const [date, setDate] = useState(todayIso())

  const sex: Sex = profile.sex === 'female' ? 'female' : 'male'
  const age = profile.birthDate ? ageFrom(profile.birthDate, todayIso()) : profile.age
  const b = bmr(weightKg, profile.heightCm, age, sex)
  const bRange = bmrRange(b)

  // Yağ oranı biliniyorsa doku enerji yoğunluğu kişiselleşiyor; yoksa
  // obezitede tipik bir varsayılan. Sabit 7700 hiçbir durumda kullanılmıyor.
  const lastWaist = measurements.data?.[measurements.data.length - 1]?.waistCm
  const bodyFat = lastWaist ? rfm(lastWaist, profile.heightCm, sex) : 32
  const rho = rhoEff(weightKg, bodyFat)
  const lean = leanMassKg(weightKg, bodyFat)

  const all = activities.data ?? []
  const today = all.filter((a) => a.date === todayIso())
  const last7 = all.filter((a) => a.date >= shiftIso(todayIso(), -6))

  const todayExpenditure = dailyExpenditure(b, today, weightKg)
  const weeklyActivityKcal = last7.reduce((s, a) => s + activityKcal(a, weightKg, b), 0)
  const avgExpenditure = b * 1.15 + weeklyActivityKcal / 7

  const goal = goalOf(profile.goal)
  const target = calorieTarget(
    avgExpenditure, b, profile.weeklyRateKg, rho, profile.calorieAdjust ?? 0, goal)
  const protein = proteinTarget(weightKg, profile.heightCm, lean)

  const slope = weightSlope(weights.map((w) => ({ date: w.date, kg: w.kg })).slice(-42))
  const check = rateCheck(
    slope, profile.weeklyRateKg, rho, profile.consecutiveCuts ?? 0, target.floored, goal)

  return (
    <>
      <div className="card">
        <h2>Enerji <span className="pill pill-accent" style={{ marginLeft: 6, verticalAlign: 'middle' }}>{GOAL_LABEL[goal]}</span></h2>
        <Prose className="card-h">
          Tek bir "aktivite seviyesi" seçeneği yok — o listelerin hakemli bir kaynağı
          yok ve bir kutucuk yanlış seçmek günde ~360 kcal, ayda 1.4 kg tahmin hatası
          demek. Bunun yerine ne yaptığını gün gün yazıyorsun.
        </Prose>

        <div className="grid g4">
          <div className="tile">
            <div className="lab">Bazal metabolizma</div>
            <div className="val">{k0(bRange.low)}–{k0(bRange.high)}</div>
            <div className="dl">kcal/gün · hata payı ±%10</div>
          </div>
          <div className="tile">
            <div className="lab">Bugünkü harcama</div>
            <div className="val">{k0(todayExpenditure)}</div>
            <div className="dl">
              {today.length ? `${today.length} aktivite kayıtlı` : 'aktivite girilmedi — sadece taban'}
            </div>
          </div>
          <div className="tile">
            <div className="lab">Günlük hedef</div>
            <div className="val">{k0(target.target)}</div>
            <div className="dl">
              {target.floored
                ? `taban ${k0(target.floor)} devrede — hedeflenen hız çıkmaz`
                : target.deficit > 0 ? `açık ${k0(target.deficit)} kcal`
                : target.deficit < 0 ? `fazlalık ${k0(-target.deficit)} kcal`
                : 'harcamayla aynı — koruma'}
              {(profile.calorieAdjust ?? 0) !== 0 &&
                ` · düzeltme ${profile.calorieAdjust > 0 ? '+' : ''}${profile.calorieAdjust}`}
            </div>
          </div>
          <div className="tile">
            <div className="lab">Protein</div>
            <div className="val">{protein.target}<span className="hero-u" style={{ fontSize: 15 }}> g</span></div>
            <div className="dl">bant {protein.low}–{protein.high} g</div>
          </div>
        </div>

        <Prose className="note">
          Egzersizle yakılan kalori günlük hedefi <strong>yükseltmiyor</strong>. Ölçülen
          telafi oranı %28, yüksek yağ kütlesinde %49 — yani yakılanın önemli kısmı
          vücut başka yerden kısarak geri alıyor. Antrenmanın asıl işi kalori yakmak
          değil, metabolizmanın düşmesini engellemek.
        </Prose>
      </div>

      {/* ── Kapalı çevrim ──────────────────────────────────────────────────── */}
      <div className="card" style={{ borderLeft: `3px solid ${verdictColor(check.verdict)}` }}>
        <h2>Gidişat</h2>
        <p className="sub">{check.message}</p>
        {check.suggestion !== 0 && (
          <button className="primary" style={{ marginTop: 12 }}
                  disabled={adjust.isPending}
                  onClick={() => adjust.mutate(check.suggestion)}>
            Hedefi {check.suggestion > 0 ? '+' : ''}{check.suggestion} kcal ayarla
          </button>
        )}
        {(profile.calorieAdjust ?? 0) !== 0 && (
          <Prose className="note">
            Şu ana kadar biriken düzeltme: {profile.calorieAdjust > 0 ? '+' : ''}
            {profile.calorieAdjust} kcal/gün
            {(profile.consecutiveCuts ?? 0) > 0 && ` · üst üste ${profile.consecutiveCuts} düşürme`}.{' '}
            <button style={{ padding: '2px 9px', fontSize: 12 }}
                    disabled={reset.isPending} onClick={() => reset.mutate()}>
              Sıfırla
            </button>
            <br />
            Kilon belirgin değiştiyse ya da aktivite düzenin oturduysa sıfırlamak
            mantıklı — düzeltme eski bir duruma göre birikmiş olabilir.
          </Prose>
        )}

        {check.verdict === 'slow' && (
          <Prose className="note">
            Statik bir formül 12 haftada 300–700 kcal/gün fazla tahmin eder — adaptif
            termogenez ve gün içi hareketin azalması yüzünden. Plato budur ve
            düzeltilmesi gerekir.
          </Prose>
        )}
      </div>

      {/* ── Aktivite kaydı ─────────────────────────────────────────────────── */}
      <div className="card">
        <h2>Aktivite kaydı</h2>
        <Prose className="card-h">
          Ne yaptıysan yaz. Tenis burada özel bir şey değil — listedeki bir satır.
        </Prose>

        <div className="row">
          <div style={{ flex: '2 1 220px' }}>
            <label className="f">Aktivite</label>
            <select value={key} onChange={(e) => setKey(e.target.value)}>
              {GROUPS.map((g) => (
                <optgroup key={g} label={g}>
                  {ACTIVITIES.filter((a) => a.group === g).map((a) => (
                    <option key={a.key} value={a.key}>{a.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div style={{ flex: '0 1 110px' }}>
            <label className="f">Dakika</label>
            <input type="number" min={1} max={600} inputMode="numeric"
                   value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </div>
          <div style={{ flex: '0 1 160px' }}>
            <label className="f">Saatten kalori (aktif)</label>
            <input type="number" min={0} max={5000} inputMode="numeric" placeholder="boş = tahmin"
                   value={kcal} onChange={(e) => setKcal(e.target.value)} />
          </div>
          <div style={{ flex: '0 1 150px' }}>
            <label className="f">Tarih</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <button className="primary" disabled={add.isPending || !minutes}
                  onClick={() => {
                    const m = Number(minutes)
                    if (!m || m < 1) return
                    add.mutate({
                      date, activityKey: key, minutes: m,
                      kcalOverride: kcal.trim() === '' ? null : Math.max(0, Number(kcal) || 0),
                    })
                    setKcal('')
                  }}>
            Ekle
          </button>
        </div>

        <Prose className="note" style={{ marginTop: 6 }}>
          {ACTIVITY_BY_KEY[key]?.name} · {ACTIVITY_BY_KEY[key]?.met} MET ·{' '}
          {Number(minutes) > 0
            ? `${kcal.trim() === '' ? '≈' : ''}${k0(activityKcal(
                { date, activityKey: key, minutes: Number(minutes),
                  kcalOverride: kcal.trim() === '' ? null : Number(kcal) || 0 },
                weightKg, b))} kcal ${kcal.trim() === '' ? 'net tahmin' : 'ölçülen'}`
            : '—'}
          {ACTIVITY_BY_KEY[key] && !ACTIVITY_BY_KEY[key].weightBearing &&
            ' · vücut ağırlığını taşımayan hareket, gerçek dinlenme hızına göre hesaplanıyor'}
        </Prose>

        {last7.length > 0 && (
          <>
            <h3>Son 7 gün</h3>
            <table>
              <thead>
                <tr><th>Tarih</th><th>Aktivite</th><th>Süre</th><th>Net kcal</th><th></th></tr>
              </thead>
              <tbody>
                {last7.map((a) => (
                  <tr key={a.id}>
                    <td>{a.date.slice(8, 10)}.{a.date.slice(5, 7)}</td>
                    <td>{ACTIVITY_BY_KEY[a.activityKey]?.name ?? a.activityKey}</td>
                    <td>{a.minutes} dk</td>
                    <td>
                      {k0(activityKcal(a, weightKg, b))}
                      {a.kcalOverride != null && <span className="pill" style={{ marginLeft: 6 }}>ölçülen</span>}
                    </td>
                    <td className="act">
                      <button onClick={() => del.mutate(a.id)}>Sil</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Prose className="note">
              Haftalık aktivite toplamı {k0(weeklyActivityKcal)} kcal — günlük ortalamaya
              {' '}{k0(weeklyActivityKcal / 7)} kcal ekliyor. Hedef bunun üstünden kuruluyor,
              tek tek günlerin üstünden değil: bir gün tenis oynayıp ertesi gün oynamamak
              günlük hedefi zıplatmamalı.
            </Prose>
          </>
        )}
      </div>
    </>
  )
}

function verdictColor(v: string): string {
  if (v === 'ontrack') return 'var(--good)'
  if (v === 'slow' || v === 'fast') return 'var(--warn)'
  if (v === 'stuck') return 'var(--s2)'
  return 'var(--grid)'
}

/** ISO tarihi n gün kaydır — yerel gün, UTC değil. */
function shiftIso(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  const p = (x: number) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
